import React, { useState, useEffect } from 'react';
import { Key, Building2, Calendar, Shield, Tag, X, AlertCircle } from 'lucide-react';
import TagSelector from '../Password/TagSelector';

function ApiKeyForm({ onSubmit, onCancel, loading = false, editingKey = null, hideHeader = false }) {
  if (process.env.NODE_ENV === 'development') {
    console.log('ApiKeyForm - editingKey:', editingKey);
  }
  const [formData, setFormData] = useState({
    keyName: '',
    keyValue: '',
    provider: '',
    keyType: 'api',
    expiresAt: '',
    environment: 'production',
    permissions: [],
    tags: [],
    note: '',
    favorite: false
  });

  const [errors, setErrors] = useState({});
  const [showKeyValue, setShowKeyValue] = useState(false);

  // 일반적인 API 제공자 목록
  const commonProviders = [
    'OpenAI', 'AWS', 'Google Cloud', 'Microsoft Azure', 
    'GitHub', 'Stripe', 'Twilio', 'SendGrid', 'Other'
  ];

  // 키 타입 옵션
  const keyTypes = [
    { value: 'api', label: 'API 키', icon: '🔌' },
    { value: 'license', label: '라이선스 키', icon: '📜' },
    { value: 'ssh', label: 'SSH 키', icon: '🔐' },
    { value: 'token', label: '토큰', icon: '🎫' },
    { value: 'secret', label: '시크릿', icon: '🤫' },
    { value: 'certificate', label: '인증서', icon: '📋' }
  ];

  // 환경 옵션
  const environments = [
    { value: 'production', label: '프로덕션', color: 'danger' },
    { value: 'development', label: '개발', color: 'warning' },
    { value: 'test', label: '테스트', color: 'info' },
    { value: 'staging', label: '스테이징', color: 'secondary' }
  ];

  useEffect(() => {
    if (editingKey) {
      setFormData({
        ...editingKey,
        keyValue: '', // 보안상 기존 키 값은 표시하지 않음
        expiresAt: editingKey.expiresAt ? 
          new Date(editingKey.expiresAt.toDate()).toISOString().split('T')[0] : '',
        permissions: editingKey.permissions || []
      });
    } else {
      // 새로 추가할 때는 폼 초기화
      setFormData({
        keyName: '',
        keyValue: '',
        provider: '',
        keyType: 'api',
        expiresAt: '',
        environment: 'production',
        permissions: [],
        tags: [],
        note: '',
        favorite: false
      });
    }
  }, [editingKey]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const processedValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({ ...prev, [name]: processedValue }));
    
    // 에러 클리어
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePermissionToggle = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.keyName.trim()) {
      newErrors.keyName = '키 이름을 입력해주세요';
    }

    // 새로 추가할 때만 키 값 필수
    if (!editingKey && !formData.keyValue.trim()) {
      newErrors.keyValue = '키 값을 입력해주세요';
    }

    if (!formData.provider.trim()) {
      newErrors.provider = '제공자를 선택하거나 입력해주세요';
    }

    // 만료일 검증 (선택사항)
    if (formData.expiresAt) {
      const expiryDate = new Date(formData.expiresAt);
      if (expiryDate < new Date()) {
        newErrors.expiresAt = '만료일은 현재 날짜 이후여야 합니다';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // 만료일을 Timestamp로 변환
      const submitData = {
        ...formData,
        expiresAt: formData.expiresAt ? new Date(formData.expiresAt) : null
      };
      onSubmit(submitData);
    }
  };

  const getKeyTypeOption = (value) => {
    return keyTypes.find(type => type.value === value) || keyTypes[0];
  };

  const getEnvironmentOption = (value) => {
    return environments.find(env => env.value === value) || environments[0];
  };

  return (
    <form onSubmit={handleSubmit} className="api-key-form" autoComplete="off">
      {!hideHeader && (
        <div className="form-header">
          <h3>{editingKey ? 'API 키 수정' : 'API 키 추가'}</h3>
          <button type="button" onClick={onCancel} className="btn-close">
            <X size={20} />
          </button>
        </div>
      )}

      <div className="form-body">
        {/* 키 이름 */}
        <div className="form-group">
          <label htmlFor="keyName">
            <Key size={16} /> 키 이름 *
          </label>
          <input
            type="text"
            id="keyName"
            name="keyName"
            value={formData.keyName || ''}
            onChange={handleInputChange}
            placeholder="예: OpenAI API Key"
            className={errors.keyName ? 'error' : ''}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            autoFocus
          />
          {errors.keyName && <span className="error-message">{errors.keyName}</span>}
        </div>

        {/* 키 값 */}
        <div className="form-group">
          <label htmlFor="keyValue">
            키 값 *
            {editingKey && <span className="label-hint"> (보안상 기존 값은 표시되지 않습니다)</span>}
          </label>
          <div className="input-with-toggle">
            <input
              type={showKeyValue ? 'text' : 'password'}
              id="keyValue"
              name="keyValue"
              value={formData.keyValue || ''}
              onChange={handleInputChange}
              placeholder={editingKey ? "변경할 경우에만 입력하세요" : "sk-..."}
              className={errors.keyValue ? 'error' : ''}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            <button
              type="button"
              className="toggle-visibility"
              onClick={() => setShowKeyValue(!showKeyValue)}
            >
              {showKeyValue ? '숨기기' : '보기'}
            </button>
          </div>
          {errors.keyValue && <span className="error-message">{errors.keyValue}</span>}
        </div>

        {/* 제공자 & 키 타입 */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="provider">
              <Building2 size={16} /> 제공자 *
            </label>
            <input
              type="text"
              id="provider"
              name="provider"
              value={formData.provider}
              onChange={handleInputChange}
              placeholder="제공자 선택 또는 입력"
              list="providers"
              className={errors.provider ? 'error' : ''}
            />
            <datalist id="providers">
              {commonProviders.map(provider => (
                <option key={provider} value={provider} />
              ))}
            </datalist>
            {errors.provider && <span className="error-message">{errors.provider}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="keyType">
              키 타입
            </label>
            <select
              id="keyType"
              name="keyType"
              value={formData.keyType}
              onChange={handleInputChange}
              className="select-with-icon"
            >
              {keyTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.icon} {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 환경 & 만료일 */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="environment">
              <Shield size={16} /> 환경
            </label>
            <select
              id="environment"
              name="environment"
              value={formData.environment}
              onChange={handleInputChange}
            >
              {environments.map(env => (
                <option key={env.value} value={env.value}>
                  {env.label}
                </option>
              ))}
            </select>
            {formData.environment === 'production' && (
              <div className="warning-message">
                <AlertCircle size={14} />
                프로덕션 키는 특별히 주의해서 관리하세요
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="expiresAt">
              <Calendar size={16} /> 만료일
            </label>
            <input
              type="date"
              id="expiresAt"
              name="expiresAt"
              value={formData.expiresAt}
              onChange={handleInputChange}
              min={new Date().toISOString().split('T')[0]}
              className={errors.expiresAt ? 'error' : ''}
            />
            {errors.expiresAt && <span className="error-message">{errors.expiresAt}</span>}
          </div>
        </div>

        {/* 권한 */}
        <div className="form-group">
          <label>
            권한
          </label>
          <div className="permissions-grid">
            {['read', 'write', 'delete', 'admin'].map(permission => (
              <label key={permission} className="permission-checkbox">
                <input
                  type="checkbox"
                  checked={formData.permissions.includes(permission)}
                  onChange={() => handlePermissionToggle(permission)}
                />
                <span>{permission}</span>
              </label>
            ))}
          </div>
        </div>

        {/* 태그 */}
        <div className="form-group">
          <label>
            <Tag size={16} /> 태그
          </label>
          <TagSelector
            selectedTags={formData.tags}
            onChange={(tags) => setFormData(prev => ({ ...prev, tags }))}
            placeholder="태그 추가 (예: 개발, 프로덕션)"
          />
        </div>

        {/* 메모 */}
        <div className="form-group">
          <label htmlFor="note">
            메모
          </label>
          <textarea
            id="note"
            name="note"
            value={formData.note}
            onChange={handleInputChange}
            placeholder="사용 목적, 제한사항 등을 기록하세요"
            rows="3"
          />
        </div>

        {/* 즐겨찾기 */}
        <div className="form-group checkbox-group">
          <label>
            <input
              type="checkbox"
              name="favorite"
              checked={formData.favorite}
              onChange={handleInputChange}
            />
            즐겨찾기에 추가
          </label>
        </div>
      </div>

      <div className="form-footer">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          취소
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? '저장 중...' : (editingKey ? '수정' : '추가')}
        </button>
      </div>
    </form>
  );
}

export default ApiKeyForm;